import React, { useState, useEffect } from "react";
import Table from "../components/Table/Table";
import TopNavbar from "../components/TopNavbar/TopNavbar";
import ApiManager from "../ApiManager/ApiManager";
import NewLabel from "./NewLabel";
import SearchBar from "../components/SearchBar/SearchBar";
import { useAuth } from "../components/AuthProvider";
import {
  ShowButton,
  ShowComponent,
  hasPermission,
} from "../components/RoleBasedComponents";
import PERMISSIONS from "../permissions";
import Modal from "../components/Modal/Modal";
import ViewEntity from "../components/ViewEntity/ViewEntity";
import EditEntity from "../components/EditEntity/EditEntity";
import { useComponentLoading } from "../utils/useComponentLoading";

function Labels() {
  const { userPermissions } = useAuth();
  const { withLoading, isLoading, getMessage, getProgress } = useComponentLoading();

  const [data, setData] = useState([]);
  const [rowId, setRowId] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isDetails, setDetails] = useState(true);
  const [isEdit, setEdit] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [labelData, setLabelData] = useState([]);

  useEffect(() => {
    fetchData();
  }, [currentPage]);

  const fetchData = async () => {
    try {
      await withLoading('fetchLabels', async (onProgress) => {
        onProgress(20, 'Fetching labels...');
        const params = new URLSearchParams({
          page_size: 12,
          page: currentPage,
        });
        const response = await ApiManager.getAllLabels(params);
        onProgress(60, 'Processing data...');
        if (!response.ok) throw new Error("Failed to fetch labels");
        const labels = await response.json();
        setData(labels);
        setHasMorePages(labels.length == 12);
        onProgress(100, 'Completed');
      });
    } catch (error) {
      console.error("Error fetching labels:", error);
      setHasMorePages(false);
    }
  };

  const fetchLabelData = async () => {
    try {
      await withLoading('fetchLabelDetails', async (onProgress) => {
        onProgress(30, 'Fetching label details...');
        const query = new URLSearchParams({
          labelId: rowId,
        });
        const response = await ApiManager.getLabel(query);
        onProgress(70, 'Processing label data...');
        if (!response.ok) throw new Error("Network response was not ok");
        const json = await response.json();
        setLabelData(json);
        onProgress(100, 'Completed');
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const toggleModal = () => {
    setModalOpen(!isModalOpen);
  };

  const handleEdit = () => {
    console.log("Edit clicked for row:");
    setDetails(false);
    setEdit(true);
    setModalOpen(true);
    fetchLabelData();
  };

  const handleDetails = () => {
    console.log("Details clicked for row:");
    setDetails(true);
    setEdit(false);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const buttons = [{ label: "Details", onClick: () => handleDetails() }];

  if (hasPermission(userPermissions, PERMISSIONS.VENDOR_LABEL_UPDATE)) {
    buttons.push({ label: "Edit", onClick: () => handleEdit() });
  }

  const columns = [
    { field: "name", headerName: "Name" },
    { field: "color", headerName: "Color" },
  ];

  const editColumns = [
    { name: "name", label: "Name", type: "text" },
    { name: "color", label: "Color", type: "text" },
  ];

  const rows = (data || []).map((item) => ({
    id: item.id,
    name: item.name,
    color: item.color,
  }));

  return (
    <div>
      <TopNavbar
        title="Labels"
      />
      <div
        style={{
          display: "flex",
          justifyContent: "right",
          width: "100%",
          paddingTop: "20px",
          height: "5vh",
        }}
      >
        {/* <SearchBar /> */}
        <ShowButton permission={PERMISSIONS.VENDOR_LABEL_CREATE}>
          <NewLabel onSuccess={fetchData} />
        </ShowButton>
      </div>

      <Table
        table_columns={columns}
        view_columns={columns}
        rows={rows}
        setRowsId={setRowId}
        buttons={buttons}
        setCurrentPage={setCurrentPage}
        currentPage={currentPage}
        hasMorePages={hasMorePages}
        isLoading={isLoading('fetchLabels')}
        loadingMessage={getMessage('fetchLabels')}
        loadingProgress={getProgress('fetchLabels')}
      />

      {isModalOpen && isDetails && (
        <Modal isOpen={true} onClose={handleModalClose}>
          <ViewEntity
            id={rowId}
            fetchFunction={ApiManager.getLabel}
            columns={columns}
            entity="labelId"
          />
        </Modal>
      )}
      {isModalOpen && isEdit && (
        <Modal isOpen={true} onClose={handleModalClose}>
          <EditEntity
            id={rowId}
            updateFunction={ApiManager.updateLabel}
            prevData={labelData}
            columns={editColumns}
            toggleFunction={toggleModal}
            title="Edit Label"
          />
        </Modal>
      )}
    </div>
  );
}

export default Labels;
